import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import EnrollmentFeesSection from '@/components/EnrollmentFeesSection';
import { Phone, WhatsappLogo, EnvelopeSimple, ArrowLeft, NotePencil, CalendarBlank, ChatCircle, Clock, X, Trash, ArrowsLeftRight, Megaphone, Link as LinkIcon, MapPin, DeviceMobile, Copy, Check } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';

const STAGES = ['new_inquiry', 'contacted', 'qualified', 'demo_scheduled', 'demo_attended', 'application', 'fee_pending', 'enrolled', 'lost', 'on_hold'];
const STAGE_ACCENT = {
  new_inquiry: '#2B6EF0', contacted: '#06B6D4', qualified: '#8B5CF6',
  demo_scheduled: '#E5A100', demo_attended: '#F97316', application: '#6366F1',
  fee_pending: '#EAB308', enrolled: '#10A882', lost: '#EF4444', on_hold: '#808080',
};

// Mirrors backend LOSS_REASONS in backend/routes/lead_routes.py — values must match exactly.
const LOSS_REASONS = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'joined_competitor', label: 'Chose a competitor' },
  { value: 'no_response', label: 'No response' },
  { value: 'branch_too_far', label: 'Branch too far' },
  { value: 'timing_mismatch', label: 'Timing mismatch' },
  { value: 'academic_mismatch', label: 'Academic mismatch' },
  { value: 'parent_declined', label: 'Parent declined' },
  { value: 'duplicate', label: 'Duplicate lead' },
  { value: 'fake_lead', label: 'Fake lead' },
  { value: 'other', label: 'Other' },
];

const ACT_ICONS = { note: NotePencil, call: Phone, whatsapp: WhatsappLogo, email: EnvelopeSimple, stage_change: Clock, task: CalendarBlank, event: CalendarBlank, field_edit: NotePencil };
const ACT_COLOR = { note: '#808080', call: '#10A882', whatsapp: '#25D366', email: '#2B6EF0', stage_change: '#8B5CF6', task: '#E5A100', event: '#E5A100', field_edit: '#EC4899' };

// ADM-12/ADM-32: which pipeline stages unlock the fee/invoice functionality inside
// the "Enrollment & Fees" card (create-invoice form) vs. read-mostly ledger
// (stage === 'enrolled') vs. locked (everything before "application"). The card
// itself renders starting at 'contacted' (see the render gate below) so the
// prominent Course Interest selector is visible much earlier — this constant only
// gates the fee/invoice portion further down inside EnrollmentFeesSection.
const FEE_SECTION_ACTIVE_STAGES = ['application', 'fee_pending'];

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [showScheduleDemo, setShowScheduleDemo] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showLostConfirm, setShowLostConfirm] = useState(false);
  const [lossReason, setLossReason] = useState('');
  const [reactivating, setReactivating] = useState(false);
  const [courses, setCourses] = useState([]);

  const fetchData = async () => {
    try {
      const [lRes, aRes] = await Promise.all([api.get(`/api/leads/${id}`), api.get(`/api/leads/${id}/activities`)]);
      setLead(lRes.data);
      setActivities(aRes.data.activities || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchCalendarEvents = async () => {
    try {
      const res = await api.get(`/api/calendar/events?lead_id=${id}`);
      setCalendarEvents(res.data.events || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchData(); fetchCalendarEvents(); }, [id]);

  // ADM-30/ADM-32: course catalog fetched once on mount and passed down to
  // EnrollmentFeesSection (its prominent "Course Interest" selector + the
  // enrollment form's course picker), so it isn't fetched a second time there.
  useEffect(() => {
    api.get('/api/courses').then(({ data }) => setCourses(data.courses || [])).catch(console.error);
  }, []);

  const handleStageChange = async (stage) => {
    // Lost is a one-way-feeling transition for the counselor, so gate it behind a confirm
    // instead of firing immediately like every other stage button.
    if (stage === 'lost') { setLossReason(''); setShowLostConfirm(true); return; }
    try {
      await api.put(`/api/leads/${id}/stage`, { stage });
      toast.success('Stage updated');
      fetchData();
    } catch (e) {
      // e.g. backend 400s "A next follow-up date is required..." when moving into
      // demo_scheduled/demo_attended without one on record — surface it, not a generic "Failed".
      toast.error(e.response?.data?.detail || 'Failed to update stage');
    }
  };

  const confirmMarkLost = async () => {
    try {
      await api.put(`/api/leads/${id}/stage`, { stage: 'lost', loss_reason: lossReason || undefined });
      setShowLostConfirm(false);
      toast.success(`${lead.student_name} marked Lost. Moved out of active work — reactivate anytime.`);
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await api.post(`/api/leads/${id}/reactivate`);
      toast.success(`${lead.student_name} reactivated — back in your active work.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate');
    } finally { setReactivating(false); }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!noteContent.trim()) return;
    try { await api.post(`/api/leads/${id}/activities`, { type: noteType, content: noteContent }); setNoteContent(''); toast.success('Added'); fetchData(); } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/api/calendar/events/${eventId}`);
      toast.success('Event cancelled');
      fetchCalendarEvents();
      fetchData();
    } catch (e) { toast.error('Failed to cancel event'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground font-mono">loading...</div>;
  if (!lead) return <div className="text-sm text-muted-foreground">Lead not found</div>;

  const accent = STAGE_ACCENT[lead.stage] || '#808080';

  return (
    <div data-testid="lead-detail-page" className="stagger-in">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} data-testid="back-to-leads" className="h-8 px-2 rounded-lg text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-extrabold text-foreground tracking-tight">{lead.student_name}</h1>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded" style={{ color: accent, backgroundColor: `${accent}15` }}>
              <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: accent }} />
              {lead.stage?.replace('_', ' ')}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">{lead.source} &middot; {lead.city}, {lead.area}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" data-testid="transfer-lead-btn" onClick={() => setShowTransfer(true)} className="h-8 rounded-lg text-xs gap-1" style={{ color: '#6366F1', borderColor: '#6366F140' }}><ArrowsLeftRight size={12} /> Transfer</Button>
          <Button variant="outline" size="sm" data-testid="schedule-demo-btn" onClick={() => setShowScheduleDemo(true)} className="h-8 rounded-lg text-xs gap-1" style={{ color: '#E5A100', borderColor: '#E5A10040' }}><CalendarBlank size={12} /> Schedule Demo</Button>
          <Button variant="outline" size="sm" data-testid="lead-call-btn" className="h-8 rounded-lg text-xs gap-1"><Phone size={12} /> Call</Button>
          <Button variant="outline" size="sm" data-testid="lead-whatsapp-btn" className="h-8 rounded-lg text-xs gap-1" style={{ color: '#25D366', borderColor: '#25D36640' }}><WhatsappLogo size={12} /> WhatsApp</Button>
          <Button variant="outline" size="sm" data-testid="lead-email-btn" className="h-8 rounded-lg text-xs gap-1"><EnvelopeSimple size={12} /> Email</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main */}
        <div className="lg:col-span-2 space-y-4">
          {/* Stage selector — swapped for a reactivation banner once the lead is Lost */}
          <Card className="border border-border rounded-xl">
            <CardContent className="p-4">
              {lead.stage === 'lost' ? (
                <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="lost-reactivate-banner">
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] mb-1.5 font-bold">Pipeline Stage</p>
                    <p className="text-[12px] text-foreground">
                      This lead is marked <span className="font-bold" style={{ color: '#EF4444' }}>Lost</span>. History is preserved.
                    </p>
                  </div>
                  <Button
                    variant="outline" size="sm" onClick={handleReactivate} disabled={reactivating}
                    data-testid="reactivate-lead-btn"
                    className="h-8 rounded-lg text-xs gap-1.5 font-bold"
                    style={{ color: '#10A882', borderColor: '#10A88240' }}
                  >
                    {reactivating ? 'Reactivating...' : '↺ Reactivate'}
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] mb-2.5 font-bold">Pipeline Stage</p>
                  <div className="flex flex-wrap gap-1.5">
                    {STAGES.map(s => {
                      const sa = STAGE_ACCENT[s] || '#808080';
                      const active = lead.stage === s;
                      return (
                        <button key={s} onClick={() => handleStageChange(s)} data-testid={`stage-btn-${s}`}
                          className="text-[10px] px-3 py-1.5 rounded-lg border transition-all font-semibold uppercase tracking-wider"
                          style={{
                            backgroundColor: active ? sa : 'transparent',
                            color: active ? '#fff' : 'hsl(var(--muted-foreground))',
                            borderColor: active ? sa : 'hsl(var(--border))',
                          }}
                        >
                          {s.replace('_', ' ')}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Enrollment & Fees — ADM-12: fee/enrollment ledger now lives inline on the
              profile instead of only inside the separate EnrollStudentDialog modal, so
              enrolling never re-asks for student name/course (the duplicate-entry
              complaint). Gated by pipeline stage. */}
          <Card className="border border-border rounded-xl">
            <CardContent className="p-4">
              <h3 className="text-[9px] font-bold text-muted-foreground mb-3 uppercase tracking-[0.12em]">Enrollment &amp; Fees</h3>
              {lead.stage !== 'new_inquiry' ? (
                <EnrollmentFeesSection
                  lead={lead}
                  onChange={fetchData}
                  onEnrolled={fetchData}
                  feesUnlocked={FEE_SECTION_ACTIVE_STAGES.includes(lead.stage) || lead.stage === 'enrolled'}
                  courses={courses}
                />
              ) : (
                <p className="text-[12px] text-muted-foreground/70" data-testid="enrollment-fees-locked">
                  🔒 Course interest and enrollment open up once this lead has been contacted.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events for this Lead */}
          {calendarEvents.length > 0 && (
            <Card className="border border-border rounded-xl" data-testid="lead-calendar-events">
              <CardContent className="p-4">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] mb-2.5 font-bold">Scheduled Events</p>
                <div className="space-y-0">
                  {calendarEvents.map((evt) => (
                    <div key={evt._id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#E5A10015', color: '#E5A100' }}>
                        <CalendarBlank size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] text-foreground font-medium">{evt.summary}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                          {evt.start_time ? new Date(evt.start_time).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                          {evt.location ? ` · ${evt.location}` : ''}
                          {evt.google_link ? <> &middot; <a href={evt.google_link} target="_blank" rel="noopener noreferrer" className="underline">Google Calendar</a></> : ''}
                        </p>
                      </div>
                      <button onClick={() => handleDeleteEvent(evt._id)} data-testid={`cancel-event-${evt._id}`} className="text-muted-foreground hover:text-red-500 transition-colors p-1">
                        <Trash size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Activity */}
          <Card className="border border-border rounded-xl">
            <CardContent className="p-4">
              <form onSubmit={handleAddActivity} className="flex gap-2">
                <select value={noteType} onChange={e => setNoteType(e.target.value)} data-testid="activity-type" className="h-9 rounded-lg text-xs border border-border px-2 bg-card text-foreground font-mono">
                  <option value="note">Note</option><option value="call">Call</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="task">Task</option>
                </select>
                <Input value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Add activity..." data-testid="activity-input" className="h-9 rounded-lg text-xs flex-1" />
                <Button type="submit" size="sm" data-testid="add-activity-btn" className="h-9 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#E04B2A' }}>Add</Button>
              </form>
            </CardContent>
          </Card>

          {/* Timeline */}
          <div data-testid="lead-timeline">
            {activities.map((a, i) => {
              const Icon = ACT_ICONS[a.type] || ChatCircle;
              const color = ACT_COLOR[a.type] || '#808080';
              return (
                <div key={i} className="flex gap-3 py-3 border-b border-border last:border-0">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15`, color }}>
                    <Icon size={13} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-foreground">{a.content}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                      {a.user_name} &middot; {a.type?.replace('_', ' ')} &middot; {a.created_at ? new Date(a.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                </div>
              );
            })}
            {activities.length === 0 && <p className="text-xs text-muted-foreground text-center py-8 font-mono">no activities yet</p>}
          </div>
        </div>

        {/* Right - Profile */}
        <div className="space-y-4">
          <EditableInfoCard title="Student Info" lead={lead} onSaved={fetchData} fields={[
            { field: 'student_name', label: 'Name' },
            { field: 'grade', label: 'Grade' },
            { field: 'board', label: 'Board' },
            { field: 'preferred_timing', label: 'Timing' },
            { field: 'email', label: 'Email', type: 'email' },
            { field: 'student_phone', label: 'Phone', type: 'tel' },
            { field: 'dob', label: 'DOB', type: 'date' },
            { field: 'address', label: 'Address' },
          ]} />
          <EditableInfoCard title="Parent / Guardian" lead={lead} onSaved={fetchData} fields={[
            { field: 'parent_name', label: 'Name' },
            { field: 'parent_phone', label: 'Phone', type: 'tel' },
            { field: 'whatsapp', label: 'WhatsApp', type: 'tel' },
          ]} />
          <AttributionCard lead={lead} />
          <InfoCard title="Tracking" rows={[
            ['Source', lead.source], ['Campaign', lead.campaign], ['Medium', lead.medium], ['Consent', lead.consent_status],
          ]} />
          <InfoCard title="Dates" rows={[
            ['Inquiry', lead.inquiry_date ? new Date(lead.inquiry_date).toLocaleDateString('en-IN') : null],
            ['Last Contact', lead.last_contacted ? new Date(lead.last_contacted).toLocaleDateString('en-IN') : null],
          ]} />
          <NextFollowUpCard lead={lead} onSaved={fetchData} />
        </div>
      </div>

      {/* Schedule Demo Dialog */}
      {showScheduleDemo && (
        <ScheduleDemoDialog
          lead={lead}
          onClose={() => setShowScheduleDemo(false)}
          onCreated={() => { setShowScheduleDemo(false); fetchCalendarEvents(); fetchData(); }}
        />
      )}

      {/* Transfer Lead Dialog */}
      {showTransfer && (
        <TransferLeadDialog
          leadIds={[lead.id]}
          onClose={() => setShowTransfer(false)}
          onTransferred={() => { setShowTransfer(false); fetchData(); }}
        />
      )}

      {/* Mark as Lost confirmation — recoverable, so kept low-drama (no destructive red fill) */}
      <AlertDialog open={showLostConfirm} onOpenChange={setShowLostConfirm}>
        <AlertDialogContent data-testid="mark-lost-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {lead.student_name} as Lost?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll move out of your active work, but nothing is deleted — history, notes and fees are kept, and you can reactivate them anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Reason (optional)</label>
            <select
              value={lossReason}
              onChange={e => setLossReason(e.target.value)}
              data-testid="loss-reason-select"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono"
            >
              <option value="">Select a reason...</option>
              {LOSS_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-lost-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="mark-lost-confirm"
              onClick={(e) => { e.preventDefault(); confirmMarkLost(); }}
              className="bg-transparent text-foreground border border-input shadow-sm hover:bg-accent hover:text-accent-foreground"
            >
              Mark as Lost
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ScheduleDemoDialog({ lead, onClose, onCreated }) {
  const [summary, setSummary] = useState(`Demo — ${lead.student_name}`);
  const [startTime, setStartTime] = useState('');
  const [location, setLocation] = useState('');
  const [attendeeEmail, setAttendeeEmail] = useState(lead.email || '');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!summary.trim() || !startTime) { toast.error('Summary and date/time are required'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/calendar/events', {
        type: 'demo',
        lead_id: lead._id,
        summary,
        description,
        start_time: new Date(startTime).toISOString(),
        location,
        attendee_email: attendeeEmail,
      });
      toast.success('Demo scheduled');
      onCreated();
    } catch (err) {
      toast.error('Failed to schedule demo');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="schedule-demo-dialog">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold text-foreground">Schedule Demo</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Summary</label>
            <Input value={summary} onChange={e => setSummary(e.target.value)} data-testid="demo-summary" className="h-9 rounded-lg text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Date & Time</label>
            <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} data-testid="demo-start-time"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono" required />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Location (optional)</label>
            <Input value={location} onChange={e => setLocation(e.target.value)} data-testid="demo-location" placeholder="Branch / online link" className="h-9 rounded-lg text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Attendee Email</label>
            <Input value={attendeeEmail} onChange={e => setAttendeeEmail(e.target.value)} data-testid="demo-attendee-email" className="h-9 rounded-lg text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Description (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} data-testid="demo-description" rows={2}
              className="w-full rounded-lg text-xs border border-border px-3 py-2 bg-card text-foreground resize-none" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="h-8 rounded-lg text-xs">Cancel</Button>
            <Button type="submit" size="sm" disabled={submitting} data-testid="demo-submit-btn"
              className="h-8 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#2563EB' }}>
              {submitting ? 'Scheduling...' : 'Schedule'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TransferLeadDialog({ leadIds, onClose, onTransferred }) {
  const [branches, setBranches] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [counselorId, setCounselorId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/api/branches').then(r => setBranches(r.data.branches || r.data || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (!branchId) { setCounselors([]); setCounselorId(''); return; }
    api.get(`/api/users?branch_id=${branchId}&role=counselor`).then(r => setCounselors(r.data.users || r.data || [])).catch(console.error);
    setCounselorId('');
  }, [branchId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchId) { toast.error('Select a branch'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.put('/api/leads/transfer', {
        lead_ids: leadIds,
        branch_id: branchId,
        counselor_id: counselorId,
        reason,
      });
      toast.success(`Transferred ${data.transferred} lead(s)`);
      onTransferred();
    } catch (err) {
      toast.error('Transfer failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="transfer-lead-dialog">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold text-foreground">Transfer Lead{leadIds.length > 1 ? 's' : ''}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Branch *</label>
            <select value={branchId} onChange={e => setBranchId(e.target.value)} data-testid="transfer-branch-select"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono" required>
              <option value="">Select branch...</option>
              {branches.map(b => <option key={b.id || b._id} value={b.id || b._id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Counselor</label>
            <select value={counselorId} onChange={e => setCounselorId(e.target.value)} data-testid="transfer-counselor-select"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono">
              <option value="">Auto-assign (round robin)</option>
              {counselors.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Reason</label>
            <Input value={reason} onChange={e => setReason(e.target.value)} data-testid="transfer-reason" placeholder="Why is this lead being transferred?" className="h-9 rounded-lg text-xs" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="h-8 rounded-lg text-xs">Cancel</Button>
            <Button type="submit" size="sm" disabled={submitting} data-testid="transfer-submit-btn"
              className="h-8 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#2563EB' }}>
              {submitting ? 'Transferring...' : `Transfer ${leadIds.length > 1 ? leadIds.length + ' Leads' : 'Lead'}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AttributionCard({ lead }) {
  const utms = [
    ['utm_source', lead.utm_source],
    ['utm_medium', lead.utm_medium],
    ['utm_campaign', lead.utm_campaign],
    ['utm_term', lead.utm_term],
    ['utm_content', lead.utm_content],
  ].filter(([, v]) => v);

  const hasAttribution = lead.campaign_id || lead.campaign_name || lead.ad_group || lead.ad_id ||
    lead.gclid || lead.fbclid || utms.length > 0 || lead.landing_page || lead.referrer_url ||
    lead.city || lead.device_type || lead.duplicate_of;

  if (!hasAttribution && !lead.source) return null;

  const touchpoints = Array.isArray(lead.touchpoints) ? lead.touchpoints : [];

  return (
    <Card className="border border-border rounded-xl" data-testid="attribution-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone size={13} className="text-muted-foreground" />
          <h3 className="text-[9px] font-bold text-muted-foreground uppercase tracking-[0.12em]">Attribution</h3>
        </div>
        <div className="space-y-2 text-[12px]">
          {lead.source && (
            <Row label="Source"><span className="font-mono text-foreground">{lead.source}</span></Row>
          )}
          {lead.campaign_name && (
            <Row label="Campaign"><span className="text-foreground font-medium">{lead.campaign_name}</span></Row>
          )}
          {lead.campaign_id && !lead.campaign_name && (
            <Row label="Campaign ID"><span className="font-mono text-[10px] text-foreground/80 break-all">{lead.campaign_id}</span></Row>
          )}
          {lead.ad_group && <Row label="Ad Group"><span className="font-mono text-[10px] text-foreground/80">{lead.ad_group}</span></Row>}
          {lead.ad_id && <Row label="Ad ID"><span className="font-mono text-[10px] text-foreground/80 break-all">{lead.ad_id}</span></Row>}
          {lead.ad_creative && <Row label="Creative"><span className="font-mono text-[10px] text-foreground/80 break-all">{lead.ad_creative}</span></Row>}

          {utms.length > 0 && (
            <div className="pt-2 mt-2 border-t border-border">
              <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">UTM Parameters</p>
              <div className="space-y-1">
                {utms.map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2 text-[10px]">
                    <span className="font-mono text-muted-foreground w-24 shrink-0">{k}</span>
                    <span className="font-mono text-foreground/90 break-all">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(lead.gclid || lead.fbclid) && (
            <div className="pt-2 mt-2 border-t border-border space-y-1">
              {lead.gclid && (
                <div className="flex items-start gap-2 text-[10px]">
                  <span className="font-mono text-muted-foreground w-16 shrink-0">gclid</span>
                  <span className="font-mono text-foreground/90 break-all">{lead.gclid}</span>
                </div>
              )}
              {lead.fbclid && (
                <div className="flex items-start gap-2 text-[10px]">
                  <span className="font-mono text-muted-foreground w-16 shrink-0">fbclid</span>
                  <span className="font-mono text-foreground/90 break-all">{lead.fbclid}</span>
                </div>
              )}
            </div>
          )}

          {(lead.landing_page || lead.referrer_url) && (
            <div className="pt-2 mt-2 border-t border-border space-y-1.5">
              {lead.landing_page && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-0.5">Landing Page</p>
                  <a href={lead.landing_page} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-foreground/90 break-all underline hover:text-foreground inline-flex items-center gap-1">
                    <LinkIcon size={10} />{lead.landing_page}
                  </a>
                </div>
              )}
              {lead.referrer_url && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-0.5">Referrer</p>
                  <a href={lead.referrer_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-foreground/90 break-all underline hover:text-foreground inline-flex items-center gap-1">
                    <LinkIcon size={10} />{lead.referrer_url}
                  </a>
                </div>
              )}
            </div>
          )}

          {(lead.city || lead.device_type) && (
            <div className="pt-2 mt-2 border-t border-border flex items-center gap-3 text-[10px] text-muted-foreground font-mono">
              {lead.city && <span className="inline-flex items-center gap-1"><MapPin size={11} />{lead.city}</span>}
              {lead.device_type && <span className="inline-flex items-center gap-1"><DeviceMobile size={11} />{lead.device_type}</span>}
            </div>
          )}

          {lead.duplicate_of && (
            <div className="pt-2 mt-2 border-t border-border">
              <Link to={`/leads/${lead.duplicate_of}`} className="text-[11px] inline-flex items-center gap-1 text-[#2563EB] hover:underline" data-testid="duplicate-of-link">
                <Copy size={11} /> Duplicate of original lead
              </Link>
            </div>
          )}
        </div>

        {touchpoints.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Touchpoint History</p>
            <div className="space-y-1.5" data-testid="touchpoint-list">
              {touchpoints.map((tp, i) => (
                <div key={i} className="flex items-start gap-2 text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-sm mt-1.5 shrink-0" style={{ backgroundColor: '#E04B2A' }} />
                  <div className="flex-1 min-w-0">
                    <span className="text-foreground/90 font-medium">{tp.source || tp.channel || tp.type || 'touch'}</span>
                    {tp.campaign && <span className="text-muted-foreground"> &middot; {tp.campaign}</span>}
                    {tp.timestamp && (
                      <div className="text-muted-foreground font-mono mt-0.5">
                        {new Date(tp.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-muted-foreground w-20 shrink-0 text-[11px]">{label}</span>
      <span className="text-foreground flex-1 min-w-0">{children}</span>
    </div>
  );
}

// ADM-15/ADM-12: next_follow_up was read-only on the profile before this — editing
// it here (rather than only via the demo-scheduling flow) lets a counselor set/move
// a follow-up date directly, which is also how a lead avoids getting stuck when the
// backend 400s a stage change into demo_scheduled/demo_attended for lacking one.
function NextFollowUpCard({ lead, onSaved }) {
  const [value, setValue] = useState(() => toDatetimeLocalValue(lead.next_follow_up));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(toDatetimeLocalValue(lead.next_follow_up)); }, [lead.next_follow_up]);

  const handleSave = async () => {
    if (!value) { toast.error('Pick a date & time'); return; }
    setSaving(true);
    try {
      await api.put(`/api/leads/${lead.id}`, { next_follow_up: new Date(value).toISOString() });
      toast.success('Next follow-up updated');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update follow-up');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border border-border rounded-xl" data-testid="next-follow-up-card">
      <CardContent className="p-4">
        <h3 className="text-[9px] font-bold text-muted-foreground mb-2.5 uppercase tracking-[0.12em]">Next Follow-up</h3>
        <input
          type="datetime-local"
          value={value}
          onChange={e => setValue(e.target.value)}
          data-testid="next-follow-up-input"
          className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono mb-2"
        />
        <Button
          size="sm" onClick={handleSave} disabled={saving} data-testid="save-next-follow-up-btn"
          className="h-8 rounded-lg text-xs font-bold px-3 text-white w-full" style={{ backgroundColor: '#2563EB' }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <p className="text-[10px] text-muted-foreground mt-2">This student will appear in your Follow-ups when this date arrives.</p>
      </CardContent>
    </Card>
  );
}

function InfoCard({ title, rows }) {
  return (
    <Card className="border border-border rounded-xl">
      <CardContent className="p-4">
        <h3 className="text-[9px] font-bold text-muted-foreground mb-3 uppercase tracking-[0.12em]">{title}</h3>
        <div className="space-y-2 text-[12px]">
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-start gap-2">
              <span className="text-muted-foreground w-20 shrink-0 text-[11px]">{label}</span>
              <span className="text-foreground">{value || '-'}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ADM-16: Apple-Settings-style inline editing for the Student Info / Parent-Guardian
// cards — a counselor can fix a typo or add parent details without leaving the
// profile. Each row saves independently on blur/Enter via the existing blind-$set
// PUT /api/leads/{id} endpoint (already used by NextFollowUpCard), no page reload,
// just re-running fetchData() so the header <h1> (which derives from lead.student_name)
// and every other card pick up the new value together.
function EditableInfoCard({ title, lead, fields, onSaved }) {
  return (
    <Card className="border border-border rounded-xl">
      <CardContent className="p-4">
        <h3 className="text-[9px] font-bold text-muted-foreground mb-3 uppercase tracking-[0.12em]">{title}</h3>
        <div className="space-y-2.5 text-[12px]">
          {fields.map(({ field, label, type, hidden, groups }) => {
            if (hidden && hidden(lead)) return null;
            if (type === 'select') {
              return <EditableSelectRow key={field} lead={lead} field={field} label={label} groups={groups} onSaved={onSaved} />;
            }
            return <EditableRow key={field} lead={lead} field={field} label={label} type={type} onSaved={onSaved} />;
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function EditableRow({ lead, field, label, type = 'text', onSaved }) {
  const rawValue = lead ? lead[field] : undefined;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(rawValue ?? '');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Keep the local draft in sync whenever the lead is refetched (e.g. after a save
  // triggered from a sibling row, or an external update).
  useEffect(() => { if (!editing) setValue(rawValue ?? ''); }, [rawValue, editing]);

  const startEdit = () => { setValue(rawValue ?? ''); setEditing(true); };
  const cancelEdit = () => { setValue(rawValue ?? ''); setEditing(false); };

  const save = async () => {
    const next = value;
    if (next === (rawValue ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.put(`/api/leads/${lead.id}`, { [field]: next });
      setEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
  };

  return (
    <div className="group flex items-start gap-2 py-0.5" data-testid={`editable-row-${field}`}>
      <span className="text-muted-foreground w-20 shrink-0 text-[11px] pt-1">{label}</span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <Input
            autoFocus
            type={type}
            value={value}
            onChange={e => setValue(e.target.value)}
            onBlur={save}
            onKeyDown={handleKeyDown}
            disabled={saving}
            data-testid={`edit-field-${field}`}
            className="h-7 rounded-md text-[12px] px-2 py-1"
          />
        ) : (
          <button
            type="button"
            onClick={startEdit}
            data-testid={`edit-field-${field}-trigger`}
            className="w-full flex items-center justify-between gap-2 text-left rounded-md px-1 -mx-1 hover:bg-accent/50 transition-colors"
          >
            <span className="text-foreground truncate">{rawValue || '-'}</span>
            <span className="flex items-center gap-1 shrink-0">
              {justSaved && <Check size={11} weight="bold" className="text-[#10A882]" data-testid={`edit-field-${field}-saved`} />}
              <NotePencil size={11} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

// ADM-30: same interaction model as EditableRow above (click to reveal an
// editable control, save via the blind-$set PUT /api/leads/{id}, small
// saved-confirmation flash, toast.error on failure, calls onSaved()/fetchData()
// after success) but rendering a grouped <select> instead of a plain <Input>.
// `groups` is the `{ category: [course, ...] }` shape groupCoursesByCategory
// (from EnrollmentFeesSection.js) returns.
function EditableSelectRow({ lead, field, label, groups = {}, onSaved }) {
  const rawValue = lead ? lead[field] : undefined;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(rawValue ?? '');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => { if (!editing) setValue(rawValue ?? ''); }, [rawValue, editing]);

  const allOptions = Object.values(groups).flat();
  const matched = allOptions.find(c => c.id === rawValue);

  const startEdit = () => { setValue(rawValue ?? ''); setEditing(true); };
  const cancelEdit = () => { setValue(rawValue ?? ''); setEditing(false); };

  const save = async (next) => {
    if (next === (rawValue ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.put(`/api/leads/${lead.id}`, { [field]: next });
      setEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);
    save(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
  };

  return (
    <div className="group flex items-start gap-2 py-0.5" data-testid={`editable-row-${field}`}>
      <span className="text-muted-foreground w-20 shrink-0 text-[11px] pt-1">{label}</span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <select
            autoFocus
            value={value}
            onChange={handleChange}
            onBlur={cancelEdit}
            onKeyDown={handleKeyDown}
            disabled={saving}
            data-testid={`edit-field-${field}`}
            className="w-full h-7 rounded-md text-[12px] px-2 py-1 border border-border bg-card text-foreground"
          >
            <option value="">Select...</option>
            {Object.entries(groups).map(([cat, list]) => (
              <optgroup key={cat} label={cat}>
                {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            ))}
          </select>
        ) : (
          <button
            type="button"
            onClick={startEdit}
            data-testid={`edit-field-${field}-trigger`}
            className="w-full flex items-center justify-between gap-2 text-left rounded-md px-1 -mx-1 hover:bg-accent/50 transition-colors"
          >
            <span className="text-foreground truncate">{matched ? matched.name : '-'}</span>
            <span className="flex items-center gap-1 shrink-0">
              {justSaved && <Check size={11} weight="bold" className="text-[#10A882]" data-testid={`edit-field-${field}-saved`} />}
              <NotePencil size={11} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
